import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import "./PhoneField.scss";
import { isUrl } from "../../../utils/generalUtils";

const PhoneField = ({
  data,
  formValidation = true,
  formClass,
  formatters = {},
  validators = {},
  onChange,
}) => {
  const [value, setValue] = useState(""); // Original value (what gets saved)
  const [displayValue, setDisplayValue] = useState(""); // Display value (what user sees)
  const [isValid, setIsValid] = useState(formValidation);
  const [errorMessage, setErrorMessage] = useState("");
  const [multipleValues, setMultipleValues] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editingValue, setEditingValue] = useState("");

  const inputRef = useRef(null);
  const editInputRef = useRef(null);

  // Phone number formatting patterns
  const phonePatterns = {
    us: {
      mask: "(999) 999-9999",
      regex: /^\(\d{3}\) \d{3}-\d{4}$/,
      cleanRegex: /^\d{10}$/,
      format: (clean) => {
        if (clean.length >= 6) {
          return `(${clean.slice(0, 3)}) ${clean.slice(3, 6)}-${clean.slice(6, 10)}`;
        } else if (clean.length >= 3) {
          return `(${clean.slice(0, 3)}) ${clean.slice(3)}`;
        } else {
          return clean;
        }
      }
    },
    international: {
      mask: "+9 999 999 9999",
      regex: /^\+\d{1,3} \d{3} \d{3} \d{1,4}$/,
      cleanRegex: /^\d{7,15}$/,
      format: (clean) => {
        if (clean.length > 10) {
          return `+${clean.slice(0, -10)} ${clean.slice(-10, -7)} ${clean.slice(-7, -4)} ${clean.slice(-4)}`;
        }
        return `+${clean}`;
      }
    },
    simple: {
      mask: "999-999-9999",
      regex: /^\d{3}-\d{3}-\d{4}$/,
      cleanRegex: /^\d{10}$/,
      format: (clean) => {
        if (clean.length >= 6) {
          return `${clean.slice(0, 3)}-${clean.slice(3, 6)}-${clean.slice(6, 10)}`;
        } else if (clean.length >= 3) {
          return `${clean.slice(0, 3)}-${clean.slice(3)}`;
        }
        return clean;
      }
    }
  };

  // Memoized initial value calculation
  const initialValue = useMemo(() => {
    if (data?.answer?.value) {
      return data.answer.value;
    }
    if (data?.defaultValue) {
      return data.defaultValue;
    }
    return "";
  }, [data?.key, data?.answer?.value, data?.defaultValue]);

  // Enhanced input mask application with multiple mask support
  const applyInputMask = useCallback((inputValue, mask) => {
    if (!mask) return inputValue;

    // Handle multiple masks
    if (Array.isArray(mask)) {
      for (const singleMask of mask) {
        const masked = applyInputMask(inputValue, singleMask);
        if (masked.length === singleMask.length) {
          return masked;
        }
      }
      return inputValue;
    }

    let maskedValue = "";
    let valueIndex = 0;
    const cleanValue = inputValue.replace(/\D/g, ''); // Remove non-digits for phone numbers

    for (let i = 0; i < mask.length && valueIndex < cleanValue.length; i++) {
      const maskChar = mask[i];
      const inputChar = cleanValue[valueIndex];

      if (maskChar === "9" && /\d/.test(inputChar)) {
        maskedValue += inputChar;
        valueIndex++;
      } else if (maskChar === "a" && /[a-zA-Z]/.test(inputChar)) {
        maskedValue += inputChar;
        valueIndex++;
      } else if (maskChar === "*" && /[a-zA-Z0-9]/.test(inputChar)) {
        maskedValue += inputChar;
        valueIndex++;
      } else if (maskChar === "A" && /[A-Z]/.test(inputChar.toUpperCase())) {
        maskedValue += inputChar.toUpperCase();
        valueIndex++;
      } else if (
        maskChar !== "9" &&
        maskChar !== "a" &&
        maskChar !== "*" &&
        maskChar !== "A"
      ) {
        maskedValue += maskChar;
        if (inputChar === maskChar) {
          valueIndex++;
        }
      } else {
        valueIndex++;
        i--;
      }
    }

    return maskedValue;
  }, []);

  // Helper function to check if character is valid for mask position
  const isValidMaskChar = useCallback((char, maskChar) => {
    switch (maskChar) {
      case "9":
        return /\d/.test(char);
      case "a":
        return /[a-zA-Z]/.test(char);
      case "*":
        return /[a-zA-Z0-9]/.test(char);
      case "A":
        return /[A-Z]/.test(char.toUpperCase());
      default:
        return char === maskChar;
    }
  }, []);

  // Extract clean value from masked value
  const extractCleanValue = useCallback((maskedValue, mask) => {
    if (!mask) return maskedValue;

    // For phone numbers, extract only digits
    return maskedValue.replace(/\D/g, '');
  }, []);

  // Apply display mask - only affects display, doesn't change original value
  const applyDisplayMask = useCallback((inputValue, displayMask) => {
    if (!displayMask) return inputValue;

    // Handle multiple masks
    if (Array.isArray(displayMask)) {
      for (const singleMask of displayMask) {
        const masked = applyDisplayMask(inputValue, singleMask);
        // Check if we can fit the entire input into this mask
        if (inputValue.length <= singleMask.replace(/[^9a*A]/g, "").length) {
          return masked;
        }
      }
      // If no mask fits perfectly, use the first one
      return applyDisplayMask(inputValue, displayMask[0]);
    }

    let maskedValue = "";
    let valueIndex = 0;
    const cleanValue = inputValue.replace(/\D/g, ''); // Clean for phone formatting

    for (
      let i = 0;
      i < displayMask.length && valueIndex < cleanValue.length;
      i++
    ) {
      const maskChar = displayMask[i];
      const inputChar = cleanValue[valueIndex];

      if (maskChar === "9" && /\d/.test(inputChar)) {
        maskedValue += inputChar;
        valueIndex++;
      } else if (maskChar === "a" && /[a-zA-Z]/.test(inputChar)) {
        maskedValue += inputChar;
        valueIndex++;
      } else if (maskChar === "*" && /[a-zA-Z0-9]/.test(inputChar)) {
        maskedValue += inputChar;
        valueIndex++;
      } else if (maskChar === "A" && /[A-Z]/.test(inputChar.toUpperCase())) {
        maskedValue += inputChar.toUpperCase();
        valueIndex++;
      } else if (
        maskChar !== "9" &&
        maskChar !== "a" &&
        maskChar !== "*" &&
        maskChar !== "A"
      ) {
        maskedValue += maskChar;
        // Don't increment valueIndex for mask characters
      } else {
        break; // Stop if character doesn't match mask
      }
    }

    return maskedValue;
  }, []);

  // Validate input against display mask
  const isValidForDisplayMask = useCallback(
    (newChar, currentValue, displayMask) => {
      if (!displayMask) return true;

      // Handle multiple masks
      if (Array.isArray(displayMask)) {
        return displayMask.some((mask) =>
          isValidForDisplayMask(newChar, currentValue, mask)
        );
      }

      // For phone numbers, only allow digits and formatting characters
      if (!/[\d\+\-\(\)\s]/.test(newChar)) {
        return false;
      }

      // Find the next expected mask character position
      const currentDisplayValue = applyDisplayMask(currentValue, displayMask);
      const nextMaskPosition = currentDisplayValue.length;

      if (nextMaskPosition >= displayMask.length) {
        return false; // Mask is full
      }

      const nextMaskChar = displayMask[nextMaskPosition];

      // If it's a literal character, check if we need to auto-insert it
      if (
        nextMaskChar !== "9" &&
        nextMaskChar !== "a" &&
        nextMaskChar !== "*" &&
        nextMaskChar !== "A"
      ) {
        // This is a separator character, check if the input matches the next data position
        let dataPosition = nextMaskPosition + 1;
        while (dataPosition < displayMask.length) {
          const nextDataChar = displayMask[dataPosition];
          if (
            nextDataChar === "9" ||
            nextDataChar === "a" ||
            nextDataChar === "*" ||
            nextDataChar === "A"
          ) {
            return isValidMaskChar(newChar, nextDataChar);
          }
          dataPosition++;
        }
        return false;
      }

      return isValidMaskChar(newChar, nextMaskChar);
    },
    [isValidMaskChar, applyDisplayMask]
  );

  // Generic formatter application
  const applyFormatter = useCallback(
    (inputValue, formatterKey) => {
      const formatter = formatters[formatterKey];
      if (formatter && typeof formatter === "function") {
        return formatter(inputValue);
      }

      // Built-in formatters
      switch (formatterKey) {
        case "uppercase":
          return inputValue.toUpperCase();
        case "lowercase":
          return inputValue.toLowerCase();
        case "capitalize":
          return (
            inputValue.charAt(0).toUpperCase() +
            inputValue.slice(1).toLowerCase()
          );
        case "trim":
          return inputValue.trim();
        default:
          return inputValue;
      }
    },
    [formatters]
  );

  // Enhanced phone validation function
  const isValidPhone = useCallback((phone) => {
    // Remove all non-digit characters
    const cleanPhone = phone.replace(/\D/g, '');
    
    // Basic checks
    if (cleanPhone.length < 7 || cleanPhone.length > 15) {
      return false;
    }
    
    // US phone number pattern (10 digits)
    if (cleanPhone.length === 10) {
      // US validation - first digit can't be 0 or 1
      if (cleanPhone[0] === '0' || cleanPhone[0] === '1') {
        return false;
      }
      // Area code can't start with 0 or 1
      if (cleanPhone[0] === '0' || cleanPhone[0] === '1') {
        return false;
      }
      return true;
    }
    
    // International numbers (7-15 digits)
    if (cleanPhone.length >= 7 && cleanPhone.length <= 15) {
      return true;
    }
    
    return false;
  }, []);

  // Detect phone number format
  const detectPhoneFormat = useCallback((phone) => {
    const cleanPhone = phone.replace(/\D/g, '');
    
    if (cleanPhone.length === 10) {
      return 'us';
    } else if (cleanPhone.length > 10) {
      return 'international';
    }
    return 'simple';
  }, []);

  // Format phone number automatically
  const formatPhoneNumber = useCallback((phone) => {
    const cleanPhone = phone.replace(/\D/g, '');
    const format = detectPhoneFormat(phone);
    
    if (phonePatterns[format]) {
      return phonePatterns[format].format(cleanPhone);
    }
    
    return phone;
  }, [detectPhoneFormat]);

  // Generate dynamic error messages
  const generateErrorMessage = useCallback(
    (validationType, config, inputValue) => {
      const validation = data?.validate || {};

      // Return custom message if provided
      if (validation.customMessage) {
        return validation.customMessage;
      }

      // Generate dynamic messages based on validation type
      switch (validationType) {
        case "required":
          return data?.invalidMessage || "Phone number is required";
        case "minLength":
          return `Phone number must be at least ${config} digits (current: ${inputValue.replace(/\D/g, '').length})`;
        case "maxLength":
          return `Phone number must be no more than ${config} digits (current: ${inputValue.replace(/\D/g, '').length})`;
        case "pattern":
          return "Phone number format doesn't match the required pattern";
        case "phone":
          return "Please enter a valid phone number (e.g., (555) 123-4567)";
        case "custom":
          return "Invalid phone number";
        default:
          return "Invalid phone number";
      }
    },
    [data?.validate, data?.invalidMessage]
  );

  // Enhanced validation with phone-specific checks
  const validateInput = useCallback(
    (inputValue) => {
      const validation = data?.validate || {};
      const customValidator = validators[data?.key];

      // Custom validator takes precedence
      if (customValidator && typeof customValidator === "function") {
        return customValidator(inputValue, validation);
      }

      let isValid = true;
      let errorMessage = "";

      if (validation?.required && !inputValue.trim()) {
        isValid = false;
        errorMessage = generateErrorMessage("required", null, inputValue);
      } else if (inputValue.trim()) {
        const cleanPhone = inputValue.replace(/\D/g, '');
        
        // Length validation (on clean digits)
        if (validation?.minLength && cleanPhone.length < validation.minLength) {
          isValid = false;
          errorMessage = generateErrorMessage(
            "minLength",
            validation.minLength,
            inputValue
          );
        } else if (
          validation?.maxLength &&
          cleanPhone.length > validation.maxLength
        ) {
          isValid = false;
          errorMessage = generateErrorMessage(
            "maxLength",
            validation.maxLength,
            inputValue
          );
        } else {
          // Pattern validation (if custom pattern is provided)
          if (
            validation?.pattern &&
            !new RegExp(validation.pattern).test(inputValue)
          ) {
            isValid = false;
            errorMessage = generateErrorMessage(
              "pattern",
              validation.pattern,
              inputValue
            );
          } else {
            // Phone validation - this is the key difference from TextField
            if (!isValidPhone(inputValue)) {
              isValid = false;
              errorMessage = generateErrorMessage("phone", null, inputValue);
            }
          }
        }
      }

      return { isValid, errorMessage };
    },
    [
      data?.validate,
      data?.invalidMessage,
      data?.key,
      validators,
      generateErrorMessage,
      isValidPhone,
    ]
  );

  // Label positioning utilities
  const labelStyles = useMemo(() => {
    const position = data?.labelPosition || "top";
    const isHorizontal = [
      "left-left",
      "left-right",
      "right-left",
      "right-right",
    ].includes(position);

    const containerStyle = {
      top: {
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "stretch",
      },
      bottom: {
        flexDirection: "column-reverse",
        justifyContent: "flex-start",
        alignItems: "stretch",
      },
      "left-left": {
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
      },
      "left-right": {
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
      },
      "right-left": {
        flexDirection: "row-reverse",
        justifyContent: "flex-end",
        alignItems: "center",
      },
      "right-right": {
        flexDirection: "row-reverse",
        justifyContent: "flex-end",
        alignItems: "center",
      },
    }[position] || {
      flexDirection: "column",
      justifyContent: "flex-start",
      alignItems: "stretch",
    };

    const labelStyle = {
      width: isHorizontal && data?.labelWidth ? `${data.labelWidth}%` : "auto",
      textAlign: ["left-right", "right-right"].includes(position)
        ? "right"
        : "left",
      marginRight:
        ["left-left", "left-right"].includes(position) && data?.labelMargin
          ? `${data.labelMargin}%`
          : "0",
      marginLeft:
        ["right-left", "right-right"].includes(position) && data?.labelMargin
          ? `${data.labelMargin}%`
          : "0",
      paddingTop: ["top", "bottom"].includes(position) ? "12px" : "0",
    };

    return { containerStyle, labelStyle };
  }, [data?.labelPosition, data?.labelWidth, data?.labelMargin]);

  // Handle multiple values for multiple phone inputs
  const handleMultipleAdd = useCallback(() => {
    if (value.trim()) {
      // Validate the current value before adding
      const validation = validateInput(value);
      if (validation.isValid) {
        // Format the phone number before adding
        const formattedPhone = data?.inputMask ? value : formatPhoneNumber(value);
        const newValues = [...multipleValues, formattedPhone];
        setMultipleValues(newValues);
        setValue("");
        setDisplayValue("");
        setIsValid(true);
        setErrorMessage("");
        // Call onChange with array of values for multiple input
        onChange?.(newValues);
      } else {
        // Show validation error
        setIsValid(false);
        setErrorMessage(validation.errorMessage);
      }
    }
  }, [value, multipleValues, onChange, validateInput, formatPhoneNumber, data?.inputMask]);

  const handleMultipleRemove = useCallback(
    (index) => {
      if (
        multipleValues.length > 0 &&
        index >= 0 &&
        index < multipleValues.length
      ) {
        const newValues = multipleValues.filter((_, i) => i !== index);
        setMultipleValues(newValues);
        // Call onChange with updated array
        onChange?.(newValues);
      }
    },
    [multipleValues, onChange]
  );

  // Handle editing multiple values
  const handleMultipleEdit = useCallback(
    (index) => {
      if (index >= 0 && index < multipleValues.length) {
        setEditingIndex(index);
        setEditingValue(multipleValues[index]);
      }
    },
    [multipleValues]
  );

  const handleMultipleSave = useCallback(
    (index) => {
      if (editingValue.trim() && index >= 0 && index < multipleValues.length) {
        // Validate the edited value
        const validation = validateInput(editingValue.trim());
        if (validation.isValid) {
          const formattedPhone = data?.inputMask ? editingValue.trim() : formatPhoneNumber(editingValue.trim());
          const newValues = [...multipleValues];
          newValues[index] = formattedPhone;
          setMultipleValues(newValues);
          setEditingIndex(-1);
          setEditingValue("");
          onChange?.(newValues);
        }
      }
    },
    [editingValue, multipleValues, onChange, validateInput, formatPhoneNumber, data?.inputMask]
  );

  const handleMultipleCancel = useCallback(() => {
    setEditingIndex(-1);
    setEditingValue("");
  }, []);

  // Event handlers
  const handleFocus = useCallback((e) => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(
    (e) => {
      setIsFocused(false);

      let finalValue = data?.displayMask ? value : e.target.value;

      // Apply input mask on blur if specified
      if (data?.inputMask && data?.applyMaskOn === "blur") {
        finalValue = applyInputMask(finalValue, data.inputMask);
        setValue(finalValue);
        setDisplayValue(finalValue);
      }
      // Apply display mask on blur if specified
      else if (data?.displayMask && data?.applyMaskOn === "blur") {
        const displayMaskedValue = applyDisplayMask(
          finalValue,
          data.displayMask
        );
        setDisplayValue(displayMaskedValue);
        // Keep original value clean for saving
        setValue(finalValue);
      }
      // Auto-format phone number if no mask is specified
      else if (!data?.inputMask && !data?.displayMask) {
        finalValue = formatPhoneNumber(finalValue);
        setValue(finalValue);
        setDisplayValue(finalValue);
      }

      // Apply formatter on blur
      if (data?.formatter && data?.applyFormatterOn === "blur") {
        finalValue = applyFormatter(finalValue, data.formatter);
        setValue(finalValue);
        if (!data?.displayMask) {
          setDisplayValue(finalValue);
        } else {
          setDisplayValue(applyDisplayMask(finalValue, data.displayMask));
        }
      }

      // Validate and only call onChange if valid (unless multiple input)
      if (!data?.multiple) {
        const validation = validateInput(finalValue);
        setIsValid(validation.isValid);
        setErrorMessage(validation.errorMessage);

        // Only call onChange if validation passes
        if (validation.isValid && finalValue !== value) {
          onChange?.(finalValue);
        }
      }
    },
    [
      data,
      applyInputMask,
      applyDisplayMask,
      applyFormatter,
      formatPhoneNumber,
      onChange,
      value,
      validateInput,
    ]
  );

  const handleChange = useCallback(
    (e) => {
      let inputValue = e.target.value;
      let finalCleanValue = inputValue; // This will be the actual value to save
      let finalDisplayValue = inputValue; // This will be what user sees

      // For phone inputs, filter out non-digit characters except for formatting
      if (data?.inputType === "tel" && !data?.inputMask && !data?.displayMask) {
        // Allow only digits and common phone formatting characters
        inputValue = inputValue.replace(/[^\d\+\-\(\)\s]/g, '');
      }

      // Handle input vs display mask
      if (data?.inputMask && data?.applyMaskOn === "change") {
        // Input mask affects both original and display value
        const maskedValue = applyInputMask(inputValue, data.inputMask);
        finalCleanValue = maskedValue;
        finalDisplayValue = maskedValue;
      } else if (data?.displayMask && data?.applyMaskOn === "change") {
        // For display mask, we need to handle character validation
        const currentCleanValue = value;
        const currentDisplayValue = displayValue;

        // Check if this is a deletion
        if (inputValue.length < currentDisplayValue.length) {
          // Handle deletion - extract clean value from the new display value
          finalCleanValue = extractCleanValue(inputValue, data.displayMask);
          finalDisplayValue = inputValue;
        } else if (inputValue.length > currentDisplayValue.length) {
          // Handle addition - validate new character
          const newChar = inputValue.charAt(inputValue.length - 1);
          if (isValidForDisplayMask(newChar, currentCleanValue, data.displayMask)) {
            finalCleanValue = currentCleanValue + newChar;
            finalDisplayValue = applyDisplayMask(finalCleanValue, data.displayMask);
          } else {
            // If character is not valid, don't update anything - return early
            return;
          }
        } else {
          // Same length, might be a replacement
          finalCleanValue = extractCleanValue(inputValue, data.displayMask);
          finalDisplayValue = applyDisplayMask(finalCleanValue, data.displayMask);
        }
      } else {
        // No mask, use input value directly
        finalCleanValue = inputValue;
        finalDisplayValue = inputValue;
      }

      // Apply custom formatter (use the clean value for formatting)
      if (data?.formatter && data?.applyFormatterOn === "change") {
        finalCleanValue = applyFormatter(finalCleanValue, data.formatter);
        if (!data?.displayMask && !data?.inputMask) {
          finalDisplayValue = finalCleanValue;
        } else if (data?.displayMask) {
          finalDisplayValue = applyDisplayMask(finalCleanValue, data.displayMask);
        }
      }

      // Apply length restrictions (on clean digits for phone)
      const cleanDigits = finalCleanValue.replace(/\D/g, '');
      if (
        data?.validate?.maxLength &&
        cleanDigits.length > data.validate.maxLength
      ) {
        const truncatedDigits = cleanDigits.substring(0, data.validate.maxLength);
        // Rebuild the formatted value with truncated digits
        if (data?.inputMask) {
          finalCleanValue = applyInputMask(truncatedDigits, data.inputMask);
          finalDisplayValue = finalCleanValue;
        } else if (data?.displayMask) {
          finalCleanValue = truncatedDigits;
          finalDisplayValue = applyDisplayMask(finalCleanValue, data.displayMask);
        } else {
          finalCleanValue = formatPhoneNumber(truncatedDigits);
          finalDisplayValue = finalCleanValue;
        }
      }

      // Update state with final values
      setValue(finalCleanValue);
      setDisplayValue(finalDisplayValue);

      // Validate input (use final clean value)
      if (!data?.validateWhenHidden || !data?.hidden) {
        const validation = validateInput(finalCleanValue);
        setIsValid(validation.isValid);
        setErrorMessage(validation.errorMessage);

        // For non-multiple inputs, call onChange with the final clean value
        if (!data?.multiple && validation.isValid) {
          onChange?.(finalCleanValue);
        }
      } else if (!data?.multiple) {
        // Even if validation is skipped, still call onChange with clean value
        onChange?.(finalCleanValue);
      }
    },
    [
      data,
      applyInputMask,
      applyDisplayMask,
      applyFormatter,
      formatPhoneNumber,
      validateInput,
      onChange,
      value,
      displayValue,
      isValidForDisplayMask,
      extractCleanValue,
    ]
  );

  // Handle Enter key for multiple inputs
  const handleKeyDown = useCallback(
    (e) => {
      if (data?.multiple && e.key === "Enter") {
        e.preventDefault();
        handleMultipleAdd();
      }
      
      // Handle special phone input keys
      if (data?.inputType === "tel") {
        // Allow backspace, delete, tab, escape, enter
        if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
          // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
          (e.keyCode === 65 && e.ctrlKey) ||
          (e.keyCode === 67 && e.ctrlKey) ||
          (e.keyCode === 86 && e.ctrlKey) ||
          (e.keyCode === 88 && e.ctrlKey) ||
          // Allow home, end, left, right
          (e.keyCode >= 35 && e.keyCode <= 39)) {
          return;
        }
        
        // Ensure that it's a number or allowed phone character and stop the keypress
        if (!data?.inputMask && !data?.displayMask) {
          if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && 
              (e.keyCode < 96 || e.keyCode > 105) && 
              ![107, 109, 187, 189].includes(e.keyCode)) { // +, -, =, -
            e.preventDefault();
          }
        }
      }
    },
    [data?.multiple, data?.inputType, data?.inputMask, data?.displayMask, handleMultipleAdd]
  );

  // Initialize component
  useEffect(() => {
    setIsValid(formValidation);

    if (data?.multiple && Array.isArray(initialValue)) {
      setMultipleValues(initialValue);
      setValue("");
      setDisplayValue("");
    } else {
      setValue(initialValue);
      // If display mask is present, apply it to initial value
      if (data?.displayMask) {
        setDisplayValue(applyDisplayMask(initialValue, data.displayMask));
      } else if (data?.inputMask) {
        setDisplayValue(applyInputMask(initialValue, data.inputMask));
      } else {
        setDisplayValue(initialValue);
      }
    }

    if (data?.autofocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [
    initialValue,
    formValidation,
    data?.autofocus,
    data?.multiple,
    data?.displayMask,
    data?.inputMask,
    applyDisplayMask,
    applyInputMask,
  ]);

  // Focus edit input when editing starts
  useEffect(() => {
    if (editingIndex !== -1 && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingIndex]);

  // Don't render if hidden
  if (data?.hidden) {
    return null;
  }

  // Render label
  const renderLabel = () => {
    if (data?.hideLabel || !data?.label) return null;

    return (
      <div className="phonefield__label" style={labelStyles.labelStyle}>
        {data?.labelPrefix && (
          <span className="phonefield__label-prefix">{data.labelPrefix}</span>
        )}
        {data.label}
        {data?.validate?.required && (
          <span
            className={`phonefield__required ${
              !isValid ? "phonefield__required--error" : ""
            }`}
          >
            {" "}
            *
          </span>
        )}
        {data?.tooltip && (
          <span className="phonefield__tooltip" title={data.tooltip}>
            ℹ️
          </span>
        )}
      </div>
    );
  };

  // Render multiple values display with improved editing UI
  const renderMultipleValues = () => {
    if (!data?.multiple || multipleValues.length === 0) return null;

    return (
      <div className="phonefield__multiple-values">
        {multipleValues.map((val, index) => (
          <div key={index} className="phonefield__multiple-tag">
            {editingIndex === index ? (
              <div className="phonefield__multiple-edit">
                <input
                  ref={editInputRef}
                  type="tel"
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className="phonefield__multiple-edit-input"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleMultipleSave(index);
                    } else if (e.key === "Escape") {
                      handleMultipleCancel();
                    }
                  }}
                />
                <div className="phonefield__multiple-edit-actions">
                  <button
                    type="button"
                    onClick={() => handleMultipleSave(index)}
                    className="phonefield__multiple-save"
                    aria-label="Save changes"
                    title="Save changes"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="20,6 9,17 4,12"></polyline>
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={handleMultipleCancel}
                    className="phonefield__multiple-cancel"
                    aria-label="Cancel editing"
                    title="Cancel editing"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>
            ) : (
              <>
                <span
                  className="phonefield__multiple-text"
                  onClick={() => handleMultipleEdit(index)}
                  title="Click to edit"
                >
                  {val}
                </span>
                <button
                  type="button"
                  onClick={() => handleMultipleRemove(index)}
                  className="phonefield__multiple-remove"
                  aria-label={`Remove ${val}`}
                  title={`Remove ${val}`}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Render input with proper phone-specific features
  const renderInput = () => {
    // Determine placeholder - prioritize inputMaskPlaceholder over regular placeholder
    let placeholder = data?.placeholder || "Enter phone number";
    if (data?.inputMaskPlaceholderChar) {
      placeholder = data.inputMaskPlaceholderChar;
    } else if (data?.inputMask) {
      // Generate placeholder from mask
      placeholder = data.inputMask.replace(/9/g, '_');
    }

    const inputProps = {
      ref: inputRef,
      type: data?.inputType || "tel",
      disabled: data?.disabled || false,
      readOnly: data?.readOnly || false,
      value: displayValue,
      onChange: handleChange,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      placeholder: placeholder,
      className: `phonefield__input ${data?.customClass || ""}`,
      maxLength: data?.inputMask ? data.inputMask.length : undefined,
      autoComplete: data?.autocomplete || "tel",
      spellCheck: false, // Always false for phone numbers
      inputMode: data?.inputMode || "tel",
      "aria-invalid": !isValid,
      "aria-describedby": !isValid ? `${data?.key}-error` : undefined,
      "data-input-mask": data?.inputMask,
      "data-display-mask": data?.displayMask,
    };

    const inputWrapperClasses = [
      "phonefield__input-wrapper my-2",
      isFocused && "phonefield__input-wrapper--focused",
      !isValid && "phonefield__input-wrapper--error",
      data?.disabled && "phonefield__input-wrapper--disabled",
      data?.readOnly && "phonefield__input-wrapper--readonly",
      (data?.prefix || data?.suffix) && "phonefield__input-wrapper--with-addons",
      isValid && value.trim() && "phonefield__input-wrapper--valid",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className="phonefield__field">
        {renderMultipleValues()}

        <div className={inputWrapperClasses}>
          {/* Prefix */}
          {data?.prefix && (
            <span className="phonefield__prefix">{data.prefix}</span>
          )}

          {/* Phone Input */}
          <input {...inputProps} />

          {/* Suffix */}
          {data?.suffix && (
            <span className="phonefield__suffix">{data.suffix}</span>
          )}
        </div>

        {/* Multiple input add button */}
        {data?.multiple && (
          <button
            type="button"
            onClick={handleMultipleAdd}
            className="phonefield__multiple-add"
            disabled={!value.trim()}
          >
            Add Phone
          </button>
        )}

        {/* Error Messages */}
        {!isValid && (
          <div id={`${data?.key}-error`} className="phonefield__error">
            {errorMessage}
          </div>
        )}

        {/* Character Count for phone */}
        {data?.showCharCount && !data?.multiple && (
          <div className="phonefield__counts">
            <span className="phonefield__count">
              Digits: {displayValue.replace(/\D/g, '').length}
              {data?.validate?.maxLength && `/${data.validate.maxLength}`}
            </span>
          </div>
        )}

        {/* Description */}
        {data?.description && (
          <div className="phonefield__description">
            {isUrl(data.description) ? (
              <img
                src={data.description}
                alt="Field description"
                className="phonefield__description-image"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.parentNode.textContent = data.description;
                }}
              />
            ) : (
              <div dangerouslySetInnerHTML={{ __html: data.description }} />
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`phonefield ${formClass || ""} ${
        data?.disabled ? "phonefield--disabled" : ""
      } ${data?.readOnly ? "phonefield--readonly" : ""}`}
    >
      <div className="phonefield__container" style={labelStyles.containerStyle}>
        {renderLabel()}
        {renderInput()}
      </div>
    </div>
  );
};

export default PhoneField;